import RTable from '@ayocore/platform/client/components/RTable/RTable';
import type { RTableProps } from '@ayocore/platform/client/components/RTable/RTable.props';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { LoadingOverlay } from '@mantine/core';
import { flexRender } from '@tanstack/react-table';
import type { CellContext, ColumnDef, Row } from '@tanstack/react-table';
import logger from '@ayocore/platform/common/helpers/logger';
import { useRepositorySelector } from './useRepositoryController';
import { PAGE_SIZE_OPTIONS, TREE_CELL_FILL, TREE_TOGGLE_WIDTH } from './constants';
import { createRowKeyResolver } from './rowKey';
import type { RepositoryController } from './RepositoryController';
import type { RepositoryTableSubRows } from './types';
import { useSubRowsTree } from './useSubRowsTree';
import { SubRowIndent, SubRowsToggle } from './SubRowsToggle';
import { useTablePosition } from './useTablePosition';

export interface RepositoryTableWrapperProps<T extends Record<string, unknown>>
  extends Omit<RTableProps<T>, 'data' | 'pagination' | 'ref'> {
  controller: RepositoryController<T>;
  /** Показывать нижний блок: пагинацию и счётчик «Строк: n» / агрегаты (default: true). */
  showBottom?: boolean;
  /** Дерево раскрываемых строк (см. RepositoryTableSubRows). */
  subRows?: RepositoryTableSubRows<T>;
}

/** id колонки: явный либо выведенный TanStack-ом из accessorKey. */
function columnIdOf<T>(column: ColumnDef<T>): string | undefined {
  if (column.id) return column.id;
  return 'accessorKey' in column && column.accessorKey != null
    ? String(column.accessorKey)
    : undefined;
}

export interface RepositoryTableWrapperHandle<T extends Record<string, unknown>> {
  /**
   * Возвращает выбранные строки по состоянию rowSelection.
   * Ключи — бизнес-id (row.id), полученные из getRowId (см. ниже).
   * Если выбранная запись отсутствует на текущей странице — в результат не попадёт.
   */
  resolveSelectedRows: (rowSelection: Record<string, boolean>) => T[];
}

function RepositoryTableWrapperInner<T extends Record<string, unknown>>(
  props: RepositoryTableWrapperProps<T>,
  ref: React.Ref<RepositoryTableWrapperHandle<T>>
) {
  const {
    controller,
    id,
    columns,
    className,
    getRowId: externalGetRowId,
    showBottom = true,
    subRows,
    onCenteredRowChange,
    ...rest
  } = props;

  const rows = useRepositorySelector(controller, s => s.data) as T[];
  const total = useRepositorySelector(controller, s => s.totalCount);
  const isLoading = useRepositorySelector(controller, s => s.isLoading);
  const hasLoadedData = useRepositorySelector(controller, s => s.hasLoadedData);
  const error = useRepositorySelector(controller, s => s.error);
  const position = useTablePosition(id, hasLoadedData && !isLoading && error === null);
  const { page, pageSize } = controller.getQuery();

  // Единый резолвер ключа: галочки, персист выбора и resolveSelectedRows обязаны
  // считать ключ одинаково, иначе выбранные строки не разрешатся из rowSelection.
  const resolveKey = useMemo(() => createRowKeyResolver<T>(externalGetRowId), [externalGetRowId]);

  const pageVersion = useRepositorySelector(controller, s => s.loadVersion);
  const onlySelectedMode = useRepositorySelector(controller, s => s.onlySelectedMode);
  const tree = useSubRowsTree({
    rows,
    subRows,
    resolveKey,
    pageVersion,
    flat: onlySelectedMode,
  });

  useImperativeHandle(
    ref,
    () => ({
      // В дереве выбранные сущности лежат и среди подгруженных детей — их
      // собирает дерево; в плоском списке хватает строк страницы.
      resolveSelectedRows: (rowSelection: Record<string, boolean>) =>
        tree.enabled
          ? tree.resolveSelectedRows(rowSelection)
          : rows.filter((row, idx) => Boolean(rowSelection[resolveKey(row, idx)])),
    }),
    [rows, resolveKey, tree]
  );

  if (tree.enabled && rest.renderSubComponent) {
    // Оба механизма хотят экспандер строки, и вместе их не прочитать.
    logger.warn(
      '[RepositoryTable] subRows и renderSubComponent несовместимы — используется subRows'
    );
  }

  /**
   * Колонка дерева: шеврон у раскрываемой строки, отступ у детей. Остальные
   * колонки не трогаем — дети рисуются теми же ячейками, что и корни.
   */
  const treeColumns = useMemo<ColumnDef<T>[] | undefined>(() => {
    if (!tree.enabled || !columns) return columns;
    const targetId = tree.column ?? (columns[0] ? columnIdOf(columns[0]) : undefined);
    return columns.map(column => {
      if (columnIdOf(column) !== targetId) return column;
      return {
        ...column,
        cell: (ctx: CellContext<T, unknown>) => {
          // У колонки может не быть своей `cell` (только accessorKey) — обычно
          // её рисует defaultColumn таблицы, но ячейку мы переопределяем.
          const content = column.cell ? flexRender(column.cell, ctx) : String(ctx.getValue() ?? '');
          const { row } = ctx;
          if (row.depth > 0) {
            return <SubRowIndent depth={row.depth}>{content}</SubRowIndent>;
          }
          if (tree.isParentRow(row)) {
            return (
              <SubRowsToggle
                expanded={row.getIsExpanded()}
                state={tree.childrenState(row.id)}
                onToggle={() => row.toggleExpanded()}
              >
                {content}
              </SubRowsToggle>
            );
          }
          // Обычная корневая строка рядом с раскрываемыми — сдвиг под шеврон,
          // чтобы подписи в колонке стояли ровно. Обёртка тянется на всю ячейку
          // (TREE_CELL_FILL): иначе она растёт по тексту, и содержимое с
          // `width: 100%` (флажки в углу ячейки) меряется от текста, а не от поля.
          return tree.hasAnyParent ? (
            <div style={{ ...TREE_CELL_FILL, paddingLeft: TREE_TOGGLE_WIDTH }}>{content}</div>
          ) : (
            content
          );
        },
      };
    });
  }, [columns, tree.enabled, tree.column, tree.isParentRow, tree.childrenState, tree.hasAnyParent]);

  /** Пропсы дерева для RTable: без `subRows` — пусто, таблица плоская как раньше. */
  const treeProps = useMemo<Partial<RTableProps<T>>>(() => {
    if (!tree.enabled) return {};
    const selection = rest.enableRowSelection;
    return {
      getSubRows: tree.getSubRows,
      getRowCanExpand: (row: Row<T>) => tree.isParentRow(row),
      expanded: tree.expanded,
      onExpandedChange: tree.onExpandedChange,
      renderSubComponent: undefined,
      // Раскрываемая строка — узел-группа: её не выбирают (см. RepositoryTableSubRows).
      enableRowSelection:
        selection === undefined || selection === false
          ? selection
          : (row: Row<T>) =>
              !tree.isParentRow(row) && (typeof selection === 'function' ? selection(row) : true),
      // Клик по узлу-группе раскрывает его; потребитель получает клики только
      // по сущностям.
      onRowClick: (row: Row<T>, rowData: T) => {
        if (tree.isParentRow(row)) {
          row.toggleExpanded();
          return;
        }
        rest.onRowClick?.(row, rowData);
      },
      onRowDoubleClick: (row: Row<T>, rowData: T) => {
        if (tree.isParentRow(row)) return;
        rest.onRowDoubleClick?.(row, rowData);
      },
    };
  }, [tree, rest.enableRowSelection, rest.onRowClick, rest.onRowDoubleClick]);

  return (
    <div className={className} style={{ position: 'relative' }}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={50}
        overlayProps={{ radius: 'sm', blur: 1, backgroundOpacity: 0.35 }}
        loaderProps={{ size: 'sm' }}
      />
      <RTable<T>
        id={id}
        data={tree.data}
        columns={treeColumns}
        getRowId={tree.enabled ? tree.getRowId : resolveKey}
        pagination={{
          current: page,
          total,
          pageSize,
          showTop: false,
          showBottom,
          pageSizeOptions: [...PAGE_SIZE_OPTIONS],
          onPageSizeChange: size => controller.setPageSize(size),
        }}
        {...rest}
        {...treeProps}
        ref={position.tableRef}
        onCenteredRowChange={rowId => {
          position.onCenteredRowChange(rowId);
          onCenteredRowChange?.(rowId);
        }}
      />
    </div>
  );
}

export const RepositoryTableWrapper = forwardRef(RepositoryTableWrapperInner) as <
  T extends Record<string, unknown>,
>(
  p: RepositoryTableWrapperProps<T> & { ref?: React.Ref<RepositoryTableWrapperHandle<T>> }
) => ReturnType<typeof RepositoryTableWrapperInner>;

export default RepositoryTableWrapper;
